import { useState, useEffect, useCallback, useRef } from "react";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import PopUp from "../PopUp";
import CustomSelect from "../CustomSelect";
import useRicercaSearch from "../hooks/useRicercaSearch";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Associazioni = () => {
  const [loading, setLoading] = useState(false);
  const [associazioni, setAssociazioni] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedAssociazioniId, setSelectedAssociazioniId] = useState(null);
  const [ricerche, setRicerche] = useState([]);
  const [selectedRicerca, setSelectedRicerca] = useState(null);
  const [ricerchaId, setRicerchaId] = useState("");
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const navigate = useNavigate();
  const loadRicercaOptions = useRicercaSearch();
  const downloadRef = useRef(null);

  const fetchAssociazioni = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
      ricercaIdParam = ricerchaId,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        let response;

        if (ricercaIdParam) {
          // Custom filter for ricerca - use the ricerca-specific endpoint
          response = await api.get(`/associazioni/ricerca/${ricercaIdParam}`, {
            params: {
              page: pageParam,
              size: sizeParam,
              ...filterParams,
              ...(sortStrings.length > 0 && { sort: sortStrings.join(";") }),
            },
          });
          // Handle response - could be paginated or not
          if (response.data?.content && response.data?.page) {
            setAssociazioni(response.data.content);
            setTotalRows(response.data.page.totalElements);
          } else {
            setAssociazioni(Array.isArray(response.data) ? response.data : []);
            setTotalRows(Array.isArray(response.data) ? response.data.length : 0);
          }
        } else {
          const params = {
            page: pageParam,
            size: sizeParam,
            ...filterParams,
          };

          if (sortStrings.length > 0) {
            params.sort = sortStrings.join(";");
          }

          response = await api.get("/associazioni", { params });
          const { content = [], page: pageInfo } = response.data ?? {};
          setAssociazioni(content);
          setTotalRows(pageInfo?.totalElements ?? content.length);
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns, ricerchaId]
  );

  const fetchRicerche = async () => {
    try {
      setLoading(true);
      const response = await api.get("/ricerche");
      setRicerche(response.data.content || []);
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per visualizzare le associazioni!");
      navigate("/login");
      localStorage.clear();
    } else {
      if (userRole === "ADMIN") {
        fetchRicerche();
      }
    }
  }, [navigate]);

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") return;
    
    fetchAssociazioni({
      pageParam: page,
      sizeParam: pageSize,
    });
  }, [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns, ricerchaId, fetchAssociazioni]);

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleRowClick = (associazione) => {
    navigate(`/associazione/${associazione.idAssociazione}`);
  };

  const handleClearFilters = () => {
    setRicerchaId("");
    setSelectedRicerca(null);
    setColumnFilters([]);
    setColumnFilterFns({});
    setPage(0);
  };

  const handleDelete = async () => {
    if (!selectedAssociazioniId) return;

    try {
      const response = await api.delete(
        `/associazioni/${selectedAssociazioniId}`
      );
      if (response.status === 204) {
        toast.success("Associazione eliminata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        fetchAssociazioni();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Errore durante l'eliminazione!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        }
      );
    } finally {
      setShowPopUp(false);
      setSelectedAssociazioniId(null);
    }
  };

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/associazioni", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `associazioni.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const columns = [
    { field: "trainer.nome", header: "Nome Docente" },
    { field: "trainer.cognome", header: "Cognome Docente" },
    { field: "trainer.codiceFiscale", header: "Codice Fiscale" },
    { field: "ricerca.titoloCorso", header: "Titolo Corso" },
    { field: "ricerca.titoloModulo", header: "Titolo Modulo" },
    { field: "ricerca.statoRicerca", header: "Stato Ricerca" },
    { field: "note", header: "Note" },
    {
      header: "Azioni",
      Cell: ({ row }) => (
        <button
          className="outside-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedAssociazioniId(row.original.idAssociazione);
            setShowPopUp(true);
          }}
        >
          <i className="fa-solid fa-trash"></i> Elimina
        </button>
      ),
    },
  ];

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <PopUp
        open={showPopUp}
        setOpen={setShowPopUp}
        header="Conferma Eliminazione"
        content="Sei sicuro di voler rimuovere questa associazione?"
        deleteFunction={handleDelete}
      />

      <div className="page-header">
        <div className="page-title">Associazioni</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
          <div className="add-btn">
            <button onClick={() => navigate("/aggiungi-associazione")}>
              + Nuova associazione
            </button>
          </div>
        </div>
      </div>

      <div className="page-content associazioni">
        <div className="filters-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fa-solid fa-filter"></i>
              Filtri di Ricerca
            </div>
            {ricerchaId && (
              <button
                className="clear-filters-btn"
                onClick={handleClearFilters}
              >
                <i className="fa-solid fa-times"></i>
                Cancella Filtri
              </button>
            )}
          </div>

          <div className="card-content">
            <div className="filters-grid">
              <div className="filter-field">
                <label className="filter-label">
                  <i className="fa-solid fa-search"></i>
                  Ricerca
                </label>
                <div className="filter-input">
                  <CustomSelect
                    loadOptions={loadRicercaOptions}
                    defaultOptions={ricerche.map((ricerca) => ({
                      value: ricerca.id,
                      label: ricerca.titoloCorso ?? "",
                      ricerca,
                    }))}
                    value={selectedRicerca}
                    onChange={(selected) => {
                      setSelectedRicerca(selected);
                      setPage(0);
                      setRicerchaId(selected?.ricerca?.id || selected?.value || "");
                    }}
                    placeholder="Seleziona una ricerca"
                    className="filter-select"
                  />
                </div>
              </div>
            </div>

            <div className="filters-summary">
              {ricerchaId && (
                <div className="active-filters">
                  <span className="filter-tag">
                    <i className="fa-solid fa-search"></i>
                    Ricerca:{" "}
                    {selectedRicerca?.ricerca?.titoloCorso ||
                      selectedRicerca?.label ||
                      ricerchaId}
                  </span>
                </div>
              )}
              <div className="results-count">
                {associazioni.length} associazioni trovate
              </div>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={associazioni}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default Associazioni;
